// Every cooked model the view can draw, with the per-model placement data the
// renderer needs. Parcel resolves `import` specifiers statically, so both the
// JSON-module manifests and the `new URL(..., import.meta.url)` asset URLs must
// be literal — hence one explicit entry per model rather than a loop over names.
//
// Every file referenced here is un-ignored in the root `.gitignore` and
// committed: the GitHub deploy builds from a bare checkout and cannot run the
// cook/extract tools (see `../../CLAUDE.md` "Assets").

import { loadCookedModel, type CookedModel, type ModelJson, type ModelSource } from "../model";
import { rotationX, type Mat4 } from "../math";
import type { BugKind } from "../game/bugs";
import type { PowerUpName } from "../game/defs";

import standardJson from "../../assets/models/0_bug_standard.json";
import ladyJson from "../../assets/models/0_bug_lady.json";
import fatJson from "../../assets/models/0_bug_fat.json";
import speedJson from "../../assets/models/0_bug_speed.json";
import spyJson from "../../assets/models/0_bug_spy.json";
import spikeJson from "../../assets/models/0_bug_spike.json";
import blackJson from "../../assets/models/0_bug_black.json";
import appleJson from "../../assets/models/0_apple.json";
import mushroom1Json from "../../assets/models/0_plant_mushroom1.json";
import mushroom2Json from "../../assets/models/0_plant_mushroom2.json";
import mushroom3Json from "../../assets/models/0_plant_mushroom3.json";
import mushroom4Json from "../../assets/models/0_plant_mushroom4.json";
import mushroom5Json from "../../assets/models/0_plant_mushroom5.json";
import flower1Json from "../../assets/models/0_plant_flower_1.json";
import flower2Json from "../../assets/models/0_plant_flower_2.json";
import flower3Json from "../../assets/models/0_plant_flower_3.json";
import flower4Json from "../../assets/models/0_plant_flower_4.json";
import flower5Json from "../../assets/models/0_plant_flower_5.json";
import menuStartJson from "../../assets/models/2_menu_single_bugg.json";
import menuHowtoJson from "../../assets/models/21_menu_howto.json";
import menuOptionsJson from "../../assets/models/2_menu_options.json";
import menuHighscoreJson from "../../assets/models/2_menu_highscore.json";
import menuEasyJson from "../../assets/models/22_menu_easy.json";
import menuNormalJson from "../../assets/models/22_menu_normal.json";
import menuHardJson from "../../assets/models/22_menu_hard.json";
import menuBackJson from "../../assets/models/2_menu_back.json";
import menuTitleJson from "../../assets/models/0_background_title.json";

/**
 * The menu text planes plus the BUGGS! logo (plan 17). Flat textured quads,
 * not walkers: they are placed by the menu page transform, never
 * `placeOnSurface`, so `fix`/`lift` are inert for them (audited: each is a
 * single quad in the model x-y plane, readable from its -z side — the menu
 * renderer's item transform turns it to face the camera).
 */
export type MenuModelName =
  | "menuStart"
  | "menuHowto"
  | "menuOptions"
  | "menuHighscore"
  | "menuEasy"
  | "menuNormal"
  | "menuHard"
  | "menuBack"
  | "menuTitle";

export const MENU_MODEL_NAMES = [
  "menuStart",
  "menuHowto",
  "menuOptions",
  "menuHighscore",
  "menuEasy",
  "menuNormal",
  "menuHard",
  "menuBack",
  "menuTitle",
] as const satisfies readonly MenuModelName[];

/** Bug kinds plus the props the view draws with the same machinery. */
export type ModelName = BugKind | "apple" | PowerUpName | MenuModelName;

/**
 * The shared axis fix: cooked models lie in the x-y plane with head towards +y
 * and belly towards +z (`unity-overview.md` §6). Pitching +90 degrees maps the
 * head onto the path's forward and the belly onto the surface.
 *
 * The orientation audit for plan 09 checked all eight models from the +z, -z,
 * +y and side views: every one of them — including the lady bug and the apple,
 * the two suspected deviants — follows this convention, so no entry overrides
 * it. `fix` stays in the entry shape for the models still to come.
 *
 * The plan 16 audit extended the finding to the ten plants: the mushrooms
 * stand with their stalk towards +z (the "belly" side, so the standard fix
 * plants them upright on the surface) and the flowers are flat stars in the
 * x-y plane, thin along z, so the fix lays them face-out on the surface.
 */
export const MODEL_FIX: Mat4 = rotationX(Math.PI / 2);

export interface CatalogEntry {
  /** Manifest + bin URL + one URL per texture name the manifest references. */
  readonly source: ModelSource;
  /** Orientation override; `null` means the standard `MODEL_FIX`. */
  readonly fix: Mat4 | null;
  /**
   * Distance from the model origin to its lowest point (rest-pose bounds, +z
   * in model space, cooked units). The renderer lifts the model by this much
   * along the surface normal so the belly rests on the surface instead of
   * sinking into it. Measured per model with `tools/preview_model.py`, which
   * prints world-space bounds — they range from 0.20 (lady) to 1.12 (apple),
   * far too wide for one shared constant.
   */
  readonly lift: number;
}

// One literal `new URL` per texture: Parcel rewrites these at build time and a
// template-literal path silently leaves a `file:///…` URL that the browser
// refuses to fetch. Shared textures are named once here and referenced by
// several entries below.
//
// TODO(texture-cache): `5buggs_standard.png` and `eye.png` are each uploaded
// once per model that names them (4x and 5x respectively) — `loadCookedModel`
// only dedupes within one model. They are small and the roster loads once at
// startup, so a shared GL-texture cache is not worth building yet.
const TEXTURES = {
  "0_bug_black_skull.png": new URL("../../assets/textures/0_bug_black_skull.png", import.meta.url),
  "0_bug_spike.png": new URL("../../assets/textures/0_bug_spike.png", import.meta.url),
  "5buggs_standard.png": new URL("../../assets/textures/5buggs_standard.png", import.meta.url),
  "apfel_stil.png": new URL("../../assets/textures/apfel_stil.png", import.meta.url),
  "apfelblatt.png": new URL("../../assets/textures/apfelblatt.png", import.meta.url),
  "apple.png": new URL("../../assets/textures/apple.png", import.meta.url),
  "eye.png": new URL("../../assets/textures/eye.png", import.meta.url),
  "eye2.png": new URL("../../assets/textures/eye2.png", import.meta.url),
  "eye3.png": new URL("../../assets/textures/eye3.png", import.meta.url),
  "eye_black2.png": new URL("../../assets/textures/eye_black2.png", import.meta.url),
  "lady_bug.png": new URL("../../assets/textures/lady_bug.png", import.meta.url),
  // The plants name exactly one texture each; mushroom3's cooked manifest
  // references only its "b" PSD, so `0_plant_mushroom3.png` stays uncommitted.
  "0_plant_mushroom1a.png": new URL("../../assets/textures/0_plant_mushroom1a.png", import.meta.url),
  "0_plant_mushroom2a.png": new URL("../../assets/textures/0_plant_mushroom2a.png", import.meta.url),
  "0_plant_mushroom3b.png": new URL("../../assets/textures/0_plant_mushroom3b.png", import.meta.url),
  "0_plant_mushroom_4.png": new URL("../../assets/textures/0_plant_mushroom_4.png", import.meta.url),
  "0_plant_mushroom5.png": new URL("../../assets/textures/0_plant_mushroom5.png", import.meta.url),
  "0_plant_flower_1.png": new URL("../../assets/textures/0_plant_flower_1.png", import.meta.url),
  "0_plant_flower_2.png": new URL("../../assets/textures/0_plant_flower_2.png", import.meta.url),
  "0_plant_flower_3.png": new URL("../../assets/textures/0_plant_flower_3.png", import.meta.url),
  "0_plant_flower_4.png": new URL("../../assets/textures/0_plant_flower_4.png", import.meta.url),
  "0_plant_flower_5.png": new URL("../../assets/textures/0_plant_flower_5.png", import.meta.url),
  // Menu text planes (plan 17). The shipped difficulty *labels* are a rename
  // the FBX file names never caught up with: `22_menu_easy`'s own texture
  // reads NORMAL, `22_menu_normal`'s reads BUGGY, `22_menu_hard`'s BUGG HARD
  // (the highscore tables `_normal`/`_buggy`/`_bugg_hard` confirm the naming).
  // Both of the first two FBXs reference the texture *name*
  // "22_menu_normal.png"; the extractor disambiguated the two same-named
  // source PSDs, so the two entries below map that one name to different
  // files per model.
  "2_menu_single_bugg.png": new URL("../../assets/textures/2_menu_single_bugg.png", import.meta.url),
  "21_menu_howto.png": new URL("../../assets/textures/21_menu_howto.png", import.meta.url),
  "2_menu_options.png": new URL("../../assets/textures/2_menu_options.png", import.meta.url),
  "2_menu_highscore.png": new URL("../../assets/textures/2_menu_highscore.png", import.meta.url),
  "22_menu_normal.png": new URL("../../assets/textures/22_menu_normal.png", import.meta.url),
  "22_menu_normal_afdecb.png": new URL("../../assets/textures/22_menu_normal_afdecb.png", import.meta.url),
  "22_menu_hard.png": new URL("../../assets/textures/22_menu_hard.png", import.meta.url),
  "2_0_menu_back.png": new URL("../../assets/textures/2_0_menu_back.png", import.meta.url),
  "title13a.png": new URL("../../assets/textures/title13a.png", import.meta.url),
} as const;

export const MODEL_CATALOG: Record<ModelName, CatalogEntry> = {
  standard: {
    source: {
      manifest: standardJson as ModelJson,
      bin: new URL("../../assets/models/0_bug_standard.bin", import.meta.url),
      textures: { "5buggs_standard.png": TEXTURES["5buggs_standard.png"], "eye2.png": TEXTURES["eye2.png"] },
    },
    fix: null,
    lift: 0.378,
  },
  lady: {
    source: {
      manifest: ladyJson as ModelJson,
      bin: new URL("../../assets/models/0_bug_lady.bin", import.meta.url),
      textures: { "lady_bug.png": TEXTURES["lady_bug.png"], "eye.png": TEXTURES["eye.png"] },
    },
    fix: null,
    lift: 0.202,
  },
  fat: {
    source: {
      manifest: fatJson as ModelJson,
      bin: new URL("../../assets/models/0_bug_fat.bin", import.meta.url),
      textures: { "5buggs_standard.png": TEXTURES["5buggs_standard.png"], "eye.png": TEXTURES["eye.png"] },
    },
    fix: null,
    lift: 0.529,
  },
  speed: {
    source: {
      manifest: speedJson as ModelJson,
      bin: new URL("../../assets/models/0_bug_speed.bin", import.meta.url),
      textures: { "5buggs_standard.png": TEXTURES["5buggs_standard.png"], "eye3.png": TEXTURES["eye3.png"] },
    },
    fix: null,
    lift: 0.334,
  },
  spy: {
    source: {
      manifest: spyJson as ModelJson,
      bin: new URL("../../assets/models/0_bug_spy.bin", import.meta.url),
      textures: { "5buggs_standard.png": TEXTURES["5buggs_standard.png"], "eye.png": TEXTURES["eye.png"] },
    },
    fix: null,
    lift: 0.544,
  },
  spike: {
    source: {
      manifest: spikeJson as ModelJson,
      bin: new URL("../../assets/models/0_bug_spike.bin", import.meta.url),
      textures: {
        "0_bug_spike.png": TEXTURES["0_bug_spike.png"],
        "eye.png": TEXTURES["eye.png"],
      },
    },
    fix: null,
    lift: 0.497,
  },
  black: {
    source: {
      manifest: blackJson as ModelJson,
      bin: new URL("../../assets/models/0_bug_black.bin", import.meta.url),
      textures: {
        "0_bug_black_skull.png": TEXTURES["0_bug_black_skull.png"],
        "eye_black2.png": TEXTURES["eye_black2.png"],
      },
    },
    fix: null,
    lift: 0.445,
  },
  apple: {
    source: {
      manifest: appleJson as ModelJson,
      bin: new URL("../../assets/models/0_apple.bin", import.meta.url),
      textures: {
        "apple.png": TEXTURES["apple.png"],
        "apfel_stil.png": TEXTURES["apfel_stil.png"],
        "apfelblatt.png": TEXTURES["apfelblatt.png"],
      },
    },
    fix: null,
    lift: 1.124,
  },
  // The ten power-up plants (plan 16 §1). Lifts are the rest-pose +z extent
  // like every other entry; the flowers are nearly flat (lift 0.06–0.11), so
  // they hug the surface where the mushrooms stand tall on their stalks.
  mushroom1: {
    source: {
      manifest: mushroom1Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_mushroom1.bin", import.meta.url),
      textures: { "0_plant_mushroom1a.png": TEXTURES["0_plant_mushroom1a.png"] },
    },
    fix: null,
    lift: 0.855,
  },
  mushroom2: {
    source: {
      manifest: mushroom2Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_mushroom2.bin", import.meta.url),
      textures: { "0_plant_mushroom2a.png": TEXTURES["0_plant_mushroom2a.png"] },
    },
    fix: null,
    lift: 1.125,
  },
  mushroom3: {
    source: {
      manifest: mushroom3Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_mushroom3.bin", import.meta.url),
      textures: { "0_plant_mushroom3b.png": TEXTURES["0_plant_mushroom3b.png"] },
    },
    fix: null,
    lift: 0.61,
  },
  mushroom4: {
    source: {
      manifest: mushroom4Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_mushroom4.bin", import.meta.url),
      textures: { "0_plant_mushroom_4.png": TEXTURES["0_plant_mushroom_4.png"] },
    },
    fix: null,
    lift: 1.138,
  },
  mushroom5: {
    source: {
      manifest: mushroom5Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_mushroom5.bin", import.meta.url),
      textures: { "0_plant_mushroom5.png": TEXTURES["0_plant_mushroom5.png"] },
    },
    fix: null,
    lift: 0.655,
  },
  flower1: {
    source: {
      manifest: flower1Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_flower_1.bin", import.meta.url),
      textures: { "0_plant_flower_1.png": TEXTURES["0_plant_flower_1.png"] },
    },
    fix: null,
    lift: 0.108,
  },
  flower2: {
    source: {
      manifest: flower2Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_flower_2.bin", import.meta.url),
      textures: { "0_plant_flower_2.png": TEXTURES["0_plant_flower_2.png"] },
    },
    fix: null,
    lift: 0.098,
  },
  flower3: {
    source: {
      manifest: flower3Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_flower_3.bin", import.meta.url),
      textures: { "0_plant_flower_3.png": TEXTURES["0_plant_flower_3.png"] },
    },
    fix: null,
    lift: 0.077,
  },
  flower4: {
    source: {
      manifest: flower4Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_flower_4.bin", import.meta.url),
      textures: { "0_plant_flower_4.png": TEXTURES["0_plant_flower_4.png"] },
    },
    fix: null,
    lift: 0.062,
  },
  flower5: {
    source: {
      manifest: flower5Json as ModelJson,
      bin: new URL("../../assets/models/0_plant_flower_5.bin", import.meta.url),
      textures: { "0_plant_flower_5.png": TEXTURES["0_plant_flower_5.png"] },
    },
    fix: null,
    lift: 0.086,
  },
  // The menu planes (plan 17). `fix`/`lift` are inert — placement is the menu
  // page transform, not `placeOnSurface` (see `MenuModelName`).
  menuStart: {
    source: {
      manifest: menuStartJson as ModelJson,
      bin: new URL("../../assets/models/2_menu_single_bugg.bin", import.meta.url),
      textures: { "2_menu_single_bugg.png": TEXTURES["2_menu_single_bugg.png"] },
    },
    fix: null,
    lift: 0,
  },
  menuHowto: {
    source: {
      manifest: menuHowtoJson as ModelJson,
      bin: new URL("../../assets/models/21_menu_howto.bin", import.meta.url),
      textures: { "21_menu_howto.png": TEXTURES["21_menu_howto.png"] },
    },
    fix: null,
    lift: 0,
  },
  menuOptions: {
    source: {
      manifest: menuOptionsJson as ModelJson,
      bin: new URL("../../assets/models/2_menu_options.bin", import.meta.url),
      textures: { "2_menu_options.png": TEXTURES["2_menu_options.png"] },
    },
    fix: null,
    lift: 0,
  },
  menuHighscore: {
    source: {
      manifest: menuHighscoreJson as ModelJson,
      bin: new URL("../../assets/models/2_menu_highscore.bin", import.meta.url),
      textures: { "2_menu_highscore.png": TEXTURES["2_menu_highscore.png"] },
    },
    fix: null,
    lift: 0,
  },
  // easy/normal: both manifests name "22_menu_normal.png"; the files differ
  // (label rename — see the TEXTURES comment). easy shows NORMAL, normal BUGGY.
  menuEasy: {
    source: {
      manifest: menuEasyJson as ModelJson,
      bin: new URL("../../assets/models/22_menu_easy.bin", import.meta.url),
      textures: { "22_menu_normal.png": TEXTURES["22_menu_normal.png"] },
    },
    fix: null,
    lift: 0,
  },
  menuNormal: {
    source: {
      manifest: menuNormalJson as ModelJson,
      bin: new URL("../../assets/models/22_menu_normal.bin", import.meta.url),
      textures: { "22_menu_normal.png": TEXTURES["22_menu_normal_afdecb.png"] },
    },
    fix: null,
    lift: 0,
  },
  menuHard: {
    source: {
      manifest: menuHardJson as ModelJson,
      bin: new URL("../../assets/models/22_menu_hard.bin", import.meta.url),
      textures: { "22_menu_hard.png": TEXTURES["22_menu_hard.png"] },
    },
    fix: null,
    lift: 0,
  },
  menuBack: {
    source: {
      manifest: menuBackJson as ModelJson,
      bin: new URL("../../assets/models/2_menu_back.bin", import.meta.url),
      textures: { "2_0_menu_back.png": TEXTURES["2_0_menu_back.png"] },
    },
    fix: null,
    lift: 0,
  },
  menuTitle: {
    source: {
      manifest: menuTitleJson as ModelJson,
      bin: new URL("../../assets/models/0_background_title.bin", import.meta.url),
      textures: { "title13a.png": TEXTURES["title13a.png"] },
    },
    fix: null,
    lift: 0,
  },
};

export const MODEL_NAMES = Object.keys(MODEL_CATALOG) as readonly ModelName[];

/** Loads the whole roster in parallel; one loading gate for the whole view. */
export async function loadAllModels(
  gl: WebGL2RenderingContext,
): Promise<Record<ModelName, CookedModel>> {
  const loaded = await Promise.all(
    MODEL_NAMES.map(async (name) => [name, await loadCookedModel(gl, MODEL_CATALOG[name].source)] as const),
  );
  return Object.fromEntries(loaded) as Record<ModelName, CookedModel>;
}
